import type { AttendanceEvent } from '../api/types';

/**
 * Menyiapkan titik-titik peta dari event absensi — MURNI.
 *
 * Diadaptasi dari `PetaAdmin` aplikasi absensi-kurir Emguard, dan di sinilah letak satu hal yang
 * TIDAK dapat disalin. Aplikasi itu punya tabel `lokasi_kurir` berisi ping posisi berkala, jadi
 * petanya menggambar JEJAK: garis perjalanan, titik awal hijau, titik akhir merah.
 *
 * Workforce tidak punya ping berkala dan tidak akan diberi satu pun oleh PR ini. Yang ada hanyalah
 * koordinat yang MENEMPEL PADA EVENT — tempat orang menekan tombol absen. Menggambar garis di
 * antara dua titik semacam itu akan menyatakan rute yang tidak pernah diukur: garis lurus dari
 * lokasi check-in ke lokasi check-out adalah kesimpulan, bukan data.
 *
 * Karena itu peta ini menggambar TITIK, tidak pernah garis, dan tidak pernah disebut "tracking".
 */

/** Satu titik yang siap digambar. Semua nilai sudah divalidasi — komponen peta tidak menyaring. */
export interface AttendancePoint {
  readonly id: string;
  readonly latitude: number;
  readonly longitude: number;
  /**
   * Radius ketidakpastian yang DILAPORKAN PERANGKAT, bukan radius geofence.
   *
   * Null berarti perangkat tidak melaporkannya — bukan berarti nol. Keduanya digambar berbeda:
   * lihat `EventMap`.
   */
  readonly accuracyMeters: number | null;
  readonly eventType: 'CHECK_IN' | 'CHECK_OUT';
  /** Null bila lokasi tidak dievaluasi terhadap geofence mana pun. */
  readonly insideGeofence: boolean | null;
  readonly occurredAtServer: string;
}

export interface AttendanceMapData {
  readonly points: readonly AttendancePoint[];
  /**
   * Berapa event yang tidak punya koordinat yang dapat dipakai.
   *
   * Dihitung dan DITAMPILKAN, tidak dibuang diam-diam: hari yang GPS-nya mati akan terlihat sama
   * persis dengan hari tanpa absen sama sekali bila angka ini disembunyikan, dan keduanya menuntut
   * tindakan yang berbeda dari penyelia.
   */
  readonly withoutCoordinates: number;
  /**
   * Berapa event yang sudah digantikan koreksi.
   *
   * Ikut dihitung terpisah supaya "titiknya lebih sedikit dari daftar event di bawah" punya
   * penjelasan di layar, bukan tampak seperti titik yang hilang.
   */
  readonly superseded: number;
}

/** Titik tengah dan jangkauan — cukup untuk menyetel tampilan awal peta. */
export interface MapBounds {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

/**
 * Koordinat yang dapat dipakai, atau null.
 *
 * Mengembalikan nilai alih-alih boolean supaya pemanggil tidak perlu memaksa tipe (`as number`)
 * setelah pemeriksaan — pemaksaan tipe akan tetap lolos bila suatu saat pemeriksaan ini diubah,
 * dan tidak ada yang memberi tahu.
 */
function usableCoordinate(
  latitude: number | null,
  longitude: number | null,
): { readonly latitude: number; readonly longitude: number } | null {
  if (latitude === null || longitude === null) return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  // Di luar jangkauan bumi bukan sekadar "aneh": Leaflet tetap menggambarnya. Di layar itu terlihat
  // seperti peta rusak, bukan seperti data buruk — orang akan mencari masalahnya di tempat salah.
  if (latitude < -90 || latitude > 90) return null;
  if (longitude < -180 || longitude > 180) return null;
  /*
   * `0, 0` ditolak meski lolos kedua pemeriksaan di atas.
   *
   * Titik itu di Teluk Guinea, dan ia BUKAN pengukuran: perangkat yang gagal mendapat fix, atau
   * klien yang mengirim `0` ketika maksudnya `null`, menghasilkan angka yang sama persis.
   *
   * Akibatnya bukan sekadar satu titik keliru. `boundsOf` memuat SELURUH titik, jadi satu (0,0)
   * bersama satu titik Jakarta menghasilkan kotak selebar 106,8° — sekitar 11.900 km. Pada layar
   * ponsel `fitBounds` memilih zoom 1, yaitu peta dunia, dan kedua titik yang sungguh diukur
   * mengecil jadi bintik tanpa satu pun nama jalan di sekitarnya. Layar ini ada untuk memeriksa
   * geofence pada tingkat jalan; satu koordinat palsu meniadakan seluruh gunanya.
   *
   * Ditolak, bukan dibuang diam-diam: ia ikut terhitung `withoutCoordinates` dan dinyatakan di
   * layar. Aturan yang sama sudah berlaku pada `task-trail.ts` — satu produk, satu perlakuan.
   */
  if (latitude === 0 && longitude === 0) return null;
  return { latitude, longitude };
}

/**
 * Menyaring event menjadi titik yang layak digambar.
 *
 * Event yang SUDAH DIGANTIKAN koreksi tidak digambar. Baris aslinya memang tetap ada — tabel event
 * bersifat append-only — tetapi organisasi sudah memutuskan fakta di dalamnya keliru. Menggambar
 * keduanya menyatakan orang itu berada di dua tempat pada satu hari kerja, dan penyelia yang
 * memeriksa geofence justru akan melihat pelanggaran yang sudah dicabut.
 */
export function attendanceMapData(events: readonly AttendanceEvent[]): AttendanceMapData {
  const points: AttendancePoint[] = [];
  let withoutCoordinates = 0;
  let superseded = 0;

  for (const event of events) {
    if (event.supersededByEventId !== null && event.supersededByEventId !== undefined) {
      superseded += 1;
      continue;
    }
    const titik = usableCoordinate(event.latitude, event.longitude);
    if (titik === null) {
      withoutCoordinates += 1;
      continue;
    }
    points.push({
      id: event.id,
      latitude: titik.latitude,
      longitude: titik.longitude,
      accuracyMeters:
        event.accuracyMeters !== null && Number.isFinite(event.accuracyMeters)
          ? event.accuracyMeters
          : null,
      eventType: event.eventType,
      insideGeofence: event.insideGeofence,
      occurredAtServer: event.occurredAtServer,
    });
  }

  return { points, withoutCoordinates, superseded };
}

/**
 * Kotak yang memuat seluruh titik. Null bila tidak ada titik sama sekali.
 *
 * Dipakai untuk menyetel tampilan awal. Satu titik menghasilkan kotak setinggi dan selebar nol —
 * itu SAH, dan penanganannya ada di pemanggil (`fitBounds` pada kotak nol akan zoom maksimum, jadi
 * `EventMap` memakai `setView` untuk kasus itu).
 */
export function boundsOf(points: readonly AttendancePoint[]): MapBounds | null {
  let bounds: MapBounds | null = null;
  for (const p of points) {
    if (bounds === null) {
      bounds = { south: p.latitude, north: p.latitude, west: p.longitude, east: p.longitude };
      continue;
    }
    bounds = {
      south: Math.min(bounds.south, p.latitude),
      north: Math.max(bounds.north, p.latitude),
      west: Math.min(bounds.west, p.longitude),
      east: Math.max(bounds.east, p.longitude),
    };
  }
  return bounds;
}
